/**
 * External dependencies
 */
import { original, produce } from 'immer';
import {
  getPatternAsString,
  hasGradient,
  hasOpacity,
} from '@web-stories-wp/patterns';

/**
 * Internal dependencies
 */
import {
  replaceInlineGradientWithColor,
  replaceVariableWithColor,
} from '../../../savedColors/helpers';
import elementIs from '@src/web-stories-wp/story-editor/src/elements/utils/elementIs';
import type {
  Element,
  Page,
} from '@src/web-stories-wp/story-editor/src/elements/types';
import type { ReducerState, ReducerStateDraft } from './types';
import type { SavedColor } from '../../../savedColors/types';

type UpdateSavedColorProps = {
  action: 'addOrEdit' | 'delete';
  changedStyle: string | null;
  editedColor: SavedColor | Pick<SavedColor, 'color'>;
  selectedElementIds: string[];
  editingElementId?: string | null;
};

type InitialSetSavedColorsProps = {
  savedColors: SavedColor[];
};

type UpdateSavedColorsProps =
  | InitialSetSavedColorsProps
  | UpdateSavedColorProps;

/**
 * Update the local saved colors + any elements that use them.
 * Current page and selection is unchanged.
 */
function updateSavedColors(
  draft: ReducerStateDraft,
  props: UpdateSavedColorsProps
) {
  if (isInitialSavedColorsUpdate(props)) {
    draft.savedColors = props.savedColors;

    return;
  }

  const {
    action = 'addOrEdit',
    changedStyle = null,
    editedColor,
    selectedElementIds = [],
    editingElementId = null,
  } = props;

  const colorId = editedColor.color.id;
  let hexValue: string | undefined;

  draft.pages.forEach((page) => {
    const newPage: Partial<Page> = {};

    if (page.backgroundColor.id === colorId) {
      const shouldDetachColor =
        hasOpacity(editedColor.color) || action === 'delete';

      if (shouldDetachColor) {
        newPage.backgroundColor = {
          ...page.backgroundColor,
          id: undefined,
        };
      } else {
        newPage.backgroundColor = editedColor.color;
      }
    }

    const elements = page.elements.map((element) => {
      const updatedElement: Partial<Element> = {};
      const isElementInSelection = selectedElementIds.includes(element.id);
      const isEditingElement =
        editingElementId && element.id === editingElementId;

      if ('backgroundColor' in element) {
        const shouldUseSavedColor =
          element.backgroundColor.id === colorId ||
          (isElementInSelection && changedStyle === 'background');

        if (shouldUseSavedColor) {
          const shouldDetachColor = action === 'delete';

          if (shouldDetachColor) {
            // @ts-expect-error we know that backgroundColor is a property of the element
            updatedElement.backgroundColor = {
              ...editedColor.color,
              id: undefined,
            };
          } else {
            // @ts-expect-error
            updatedElement.backgroundColor = editedColor.color;
          }
        }
      }

      if ('border' in element && element.border && 'color' in element.border) {
        const shouldUseSavedColor =
          element.border.color.id === colorId ||
          (isElementInSelection && changedStyle === 'border');

        if (shouldUseSavedColor) {
          const shouldDetachColor =
            hasGradient(editedColor.color) || action === 'delete';

          if (shouldDetachColor) {
            updatedElement.border = {
              ...element.border,
              color: {
                ...element.border.color,
                id: undefined,
              },
            };
          } else {
            updatedElement.border = {
              ...element.border,
              color: editedColor.color,
            };
          }
        }
      }

      if (elementIs.text(element)) {
        if (!isEditingElement) {
          if (hexValue === undefined) {
            hexValue = getPatternAsString(editedColor.color);
          }
          const contentWithUpdatedColors = replaceVariableWithColor({
            colorId,
            detachColor: hasGradient(editedColor.color),
            hexValue,
            originalString: element.content,
            useVariable: action !== 'delete',
          });
          // @ts-expect-error we know that content is a property of the element
          updatedElement.content = replaceInlineGradientWithColor({
            colorId,
            editedColor: editedColor.color,
            originalString: contentWithUpdatedColors,
            action,
          });
        }

        if (element.color?.id && element.color.id === colorId) {
          const shouldDetachColor = action === 'delete';
          // @ts-expect-error we know that color is a property of the element
          updatedElement.color = shouldDetachColor
            ? { ...element.color, id: undefined }
            : editedColor.color;
        }

        if (
          ('textShadowColor' in element &&
            element.textShadowColor.id === colorId) ||
          (isElementInSelection && changedStyle === 'textShadow')
        ) {
          const shouldDetachColor =
            hasGradient(editedColor.color) || action === 'delete';

          if (shouldDetachColor) {
            // @ts-expect-error we know that textShadowColor is a property of the element
            updatedElement.textShadowColor = {
              ...element.textShadowColor,
              id: undefined,
            };
          } else {
            // @ts-expect-error
            updatedElement.textShadowColor = {
              id: colorId,
              color: {
                ...element.textShadowColor.color,
                ...editedColor.color.color,
              },
            };
          }
        }
      }

      const hasChanges = Object.entries(updatedElement).some(
        ([key, value]) => original(element)?.[key as keyof Element] !== value
      );
      return hasChanges ? { ...element, ...updatedElement } : element;
    });
    if (elements.some((element, index) => element !== page.elements[index])) {
      page.elements = elements;
    }
    Object.assign(page, newPage);
  });

  const savedColorIndex = draft.savedColors.findIndex(
    (color) => color.color.id === colorId
  );

  if (action === 'delete' && savedColorIndex >= 0) {
    draft.savedColors.splice(savedColorIndex, 1);
  }

  if (action === 'addOrEdit' && savedColorIndex === -1) {
    draft.savedColors.push({
      isLocal: true,
      error: null,
      shouldSync: true,
      name: '',
      ...editedColor,
    });
  }

  if (action === 'addOrEdit' && savedColorIndex >= 0) {
    draft.savedColors[savedColorIndex] = {
      ...draft.savedColors[savedColorIndex],
      ...editedColor,
    };
  }
}

function isInitialSavedColorsUpdate(
  props: UpdateSavedColorsProps
): props is InitialSetSavedColorsProps {
  return 'savedColors' in props;
}

// @ts-ignore
export default produce<ReducerState, [UpdateSavedColorsProps]>(
  updateSavedColors
);
